import React from 'react';
import { usePermissions } from '../hooks/usePermissions';

interface CanProps {
  /** Module `name` (e.g. 'yarn-inward', 'clients'). */
  module: string;
  /** Require write access (create/update/delete). Defaults to a view check. */
  write?: boolean;
  children: React.ReactNode;
  /** Rendered instead of children when access is denied. Defaults to nothing. */
  fallback?: React.ReactNode;
}

// Renders children only when the current user has the required access to `module`.
// Used to hide write controls (Add / Save / Edit / Delete) from read-only users.
export const Can: React.FC<CanProps> = ({ module, write = false, children, fallback = null }) => {
  const { canView, canWrite } = usePermissions();
  const allowed = write ? canWrite(module) : canView(module);
  return <>{allowed ? children : fallback}</>;
};

// Convenience hook for conditional logic that isn't a simple wrap.
export const useCanWrite = (module: string): boolean => {
  const { canWrite } = usePermissions();
  return canWrite(module);
};

// Create-only check (distinct from write) — e.g. taka data-entry users who can add but not view.
export const useCanCreate = (module: string): boolean => {
  const { canCreate } = usePermissions();
  return canCreate(module);
};

// Edit-only check (canUpdate) — gate Edit controls independently of Delete.
export const useCanUpdate = (module: string): boolean => {
  const { canUpdate } = usePermissions();
  return canUpdate(module);
};

// Delete-only check (canDelete) — gate Delete controls independently of Edit.
export const useCanDelete = (module: string): boolean => {
  const { canDelete } = usePermissions();
  return canDelete(module);
};

export default Can;
